import { useEffect, useState } from "react";
import AdminLayout from "../AdminLayout";
import { API_BASE } from "@/config/api";
import type { Plan } from "@/types/content";

type Draft = Omit<Plan, "orden" | "usos" | "caracteristicas"> & { usosText: string; caracteristicasText: string };

function toDraft(p?: Partial<Plan>): Draft {
  return {
    id: p?.id ?? "",
    nombre: p?.nombre ?? "",
    rango: p?.rango ?? "",
    descripcion: p?.descripcion ?? "",
    imagen_url: p?.imagen_url ?? "",
    imagen_alt: p?.imagen_alt ?? "",
    usosText: (p?.usos ?? []).join(", "),
    caracteristicasText: (p?.caracteristicas ?? []).join(", "),
  };
}

export default function AdminPlanes() {
  const [items, setItems] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    const res = await fetch(`${API_BASE}/bootstrap`);
    setItems((await res.json()).planes);
    setLoading(false);
  }

  useEffect(() => {
    load();
  }, []);

  async function handleSave() {
    if (!editing) return;
    setSaving(true);
    setError("");
    const payload = {
      ...editing,
      usos: editing.usosText.split(",").map((s) => s.trim()).filter(Boolean),
      caracteristicas: editing.caracteristicasText.split(",").map((s) => s.trim()).filter(Boolean),
    };
    const isNew = !items.some((i) => i.id === editing.id);
    try {
      const res = await fetch(isNew ? `${API_BASE}/planes` : `${API_BASE}/planes/${editing.id}`, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        setError(json?.error || "No se pudo guardar");
        return;
      }
      setEditing(null);
      await load();
    } catch {
      setError("Error de conexión.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("¿Eliminar este plan?")) return;
    await fetch(`${API_BASE}/planes/${id}`, { method: "DELETE" });
    await load();
  }

  const inputCls = "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm";
  const labelCls = "block text-xs font-medium text-foreground-600 mb-1";

  return (
    <AdminLayout>
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-2xl text-foreground-950">Planes</h1>
        <button
          type="button"
          onClick={() => setEditing(toDraft())}
          className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer"
        >
          + Agregar plan
        </button>
      </div>

      {loading ? (
        <p className="mt-6 text-sm text-foreground-500">Cargando...</p>
      ) : (
        <div className="mt-6 space-y-3">
          {items.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-lg border border-background-200 bg-background-50 p-4">
              <div>
                <p className="text-sm font-semibold text-foreground-900">{p.nombre}</p>
                <p className="text-xs text-foreground-500">{p.rango}</p>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setEditing(toDraft(p))} className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
                  Editar
                </button>
                <button type="button" onClick={() => handleDelete(p.id)} className="text-sm text-accent-600 hover:text-accent-700 cursor-pointer">
                  Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4">
          <div className="w-full max-w-lg rounded-lg bg-background-50 p-6 mt-10">
            <h2 className="font-heading font-semibold text-lg text-foreground-900">
              {items.some((i) => i.id === editing.id) ? "Editar plan" : "Nuevo plan"}
            </h2>
            {error && <div className="mt-3 rounded-md bg-accent-100 border border-accent-200 p-3 text-sm text-accent-800">{error}</div>}
            <div className="mt-4 space-y-3">
              <div>
                <label className={labelCls}>Nombre</label>
                <input className={inputCls} value={editing.nombre} onChange={(e) => setEditing({ ...editing, nombre: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Rango (ej. 12 – 40 m²)</label>
                <input className={inputCls} value={editing.rango} onChange={(e) => setEditing({ ...editing, rango: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Descripción</label>
                <textarea
                  className={inputCls}
                  rows={3}
                  value={editing.descripcion}
                  onChange={(e) => setEditing({ ...editing, descripcion: e.target.value })}
                />
              </div>
              <div>
                <label className={labelCls}>Ideal para (separado por coma)</label>
                <input className={inputCls} value={editing.usosText} onChange={(e) => setEditing({ ...editing, usosText: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Incluye (separado por coma)</label>
                <input
                  className={inputCls}
                  value={editing.caracteristicasText}
                  onChange={(e) => setEditing({ ...editing, caracteristicasText: e.target.value })}
                />
              </div>
              <div>
                <label className={labelCls}>URL de la imagen</label>
                <input className={inputCls} value={editing.imagen_url} onChange={(e) => setEditing({ ...editing, imagen_url: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Texto alternativo de la imagen</label>
                <input className={inputCls} value={editing.imagen_alt} onChange={(e) => setEditing({ ...editing, imagen_alt: e.target.value })} />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)} className="text-sm text-foreground-600 cursor-pointer">
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
              >
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
